import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Dialog } from '@headlessui/react'
import {
  PlusIcon,
  ChatBubbleLeftIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { boxApi } from '../utils/api'
import { useAuth } from '../context/AuthContext'
import ConfirmModal from './ConfirmModal'

export default function MessageWall({ boxId, canManage = false }) {
  const { currentUser } = useAuth()
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [members, setMembers] = useState([])
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [expandedMessage, setExpandedMessage] = useState(null)
  const [replyingTo, setReplyingTo] = useState(null)
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false)
  const [replyToMessage, setReplyToMessage] = useState(null)
  const [mentionState, setMentionState] = useState({ field: null, start: null, query: '' })
  const [deleteMessageModal, setDeleteMessageModal] = useState({ open: false, messageId: null })
  const [deleteReplyModal, setDeleteReplyModal] = useState({ open: false, messageId: null, replyId: null })
  const [deleteReason, setDeleteReason] = useState('')
  const [deleting, setDeleting] = useState(false)
  const subjectRef = useRef(null)
  const contentRef = useRef(null)
  const replyRef = useRef(null)
  const expandedRepliesRefs = useRef({})

  const {
    register: registerMessage,
    handleSubmit: handleSubmitMessage,
    reset: resetMessage,
    setValue: setMessageValue,
    formState: { errors: messageErrors },
  } = useForm()

  const {
    register: registerReply,
    handleSubmit: handleSubmitReply,
    reset: resetReply,
    setValue: setReplyValue,
    formState: { errors: replyErrors },
  } = useForm()

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await boxApi.getMessages(boxId)
      setMessages(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [boxId])

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  useEffect(() => {
    let cancelled = false
    const loadMembers = async () => {
      try {
        const data = await boxApi.getMembers(boxId)
        if (!cancelled) setMembers(data || [])
      } catch (_err) {
        // Mention helper is best-effort; ignore errors so messaging still works
      }
    }
    loadMembers()
    return () => {
      cancelled = true
    }
  }, [boxId])

  const mentionOptions = useMemo(() => {
    const query = (mentionState.query || '').toLowerCase()
    const filteredMembers = members
      .filter((member) => member.email)
      .filter((member) => {
        if (!query) return true
        const name = (member.name || '').toLowerCase()
        const email = (member.email || '').toLowerCase()
        return name.includes(query) || email.includes(query)
      })
      .slice(0, 10)
      .map((member) => ({
        type: 'member',
        email: member.email,
        label: member.name || member.email,
        hint: member.email,
      }))

    const showAll =
      !query || '@all'.includes(query) || 'all'.includes(query) || query === 'everyone'
    const options = showAll
      ? [
          {
            type: 'all',
            label: 'Notify everyone (@all)',
            hint: 'Sends to all members',
          },
          ...filteredMembers,
        ]
      : filteredMembers

    return options
  }, [members, mentionState.query])

  const closeMentionMenu = () => {
    setMentionState({ field: null, start: null, query: '' })
  }

  const findMentionTrigger = (value, caretIndex) => {
    const beforeCursor = value.slice(0, caretIndex)
    const match = beforeCursor.match(/@([^\s@]{0,50})$/)
    if (!match) return null
    const start = beforeCursor.lastIndexOf('@')
    return { start, query: match[1] || '' }
  }

  const handleMentionLookup = (field, value, ref) => {
    const caret = ref?.current?.selectionStart ?? value.length
    const trigger = findMentionTrigger(value, caret)
    if (!trigger) {
      if (mentionState.field === field) closeMentionMenu()
      return
    }
    setMentionState({ field, start: trigger.start, query: trigger.query })
  }

  const insertMention = (option) => {
    if (!mentionState.field) return
    const isMessageSubject = mentionState.field === 'message-subject'
    const isMessageContent = mentionState.field === 'message-content'
    const ref = isMessageSubject ? subjectRef : isMessageContent ? contentRef : replyRef
    const setter = isMessageSubject || isMessageContent ? setMessageValue : setReplyValue
    const fieldName = isMessageSubject ? 'subject' : 'content'
    const element = ref.current
    if (!element) return closeMentionMenu()

    const value = element.value || ''
    const caret = element.selectionStart ?? value.length
    const start = mentionState.start ?? caret
    const before = value.slice(0, start)
    const after = value.slice(caret)

    const mentionText = option.type === 'all' ? 'all' : `[${option.email}]`
    const insertion = `@${mentionText} `
    const updatedValue = `${before}${insertion}${after}`
    setter(fieldName, updatedValue, { shouldDirty: true, shouldTouch: true })
    closeMentionMenu()

    // Restore cursor after the inserted mention
    requestAnimationFrame(() => {
      const newPos = before.length + insertion.length
      element.focus()
      element.setSelectionRange(newPos, newPos)
    })
  }

  const renderMentionMenu = (fieldKey) => {
    if (mentionState.field !== fieldKey) return null
    if (!mentionOptions.length) return null

    return (
      <div className="absolute z-20 mt-2 w-full max-h-56 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
        {mentionOptions.map((option, idx) => (
          <button
            key={`${option.type}-${option.email || idx}`}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              insertMention(option)
            }}
            className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
          >
            <span className="text-sm font-medium text-gray-900">{option.label}</span>
            {option.hint && (
              <span className="ml-3 text-xs text-gray-500 truncate">{option.hint}</span>
            )}
          </button>
        ))}
      </div>
    )
  }

  const subjectRegistration = registerMessage('subject', {
    required: 'Subject is required',
    onChange: (e) => handleMentionLookup('message-subject', e.target.value, subjectRef),
  })

  const contentRegistration = registerMessage('content', {
    required: 'Message content is required',
    onChange: (e) => handleMentionLookup('message-content', e.target.value, contentRef),
  })

  const replyRegistration = registerReply('content', {
    required: 'Reply content is required',
    onChange: (e) => handleMentionLookup('reply-content', e.target.value, replyRef),
  })

  const onSubmitMessage = async (data) => {
    try {
      setSubmitting(true)
      setError(null)
      await boxApi.createMessage(boxId, data)
      resetMessage()
      setIsNewMessageOpen(false)
      closeMentionMenu()
      await loadMessages()
    } catch (err) {
      setError(`Failed to post message: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  const onSubmitReply = async (data) => {
    const messageIdToReply = replyingTo // Save messageId before clearing state
    try {
      setSubmitting(true)
      setError(null)
      await boxApi.createReply(boxId, messageIdToReply, data)
      resetReply()
      setIsReplyModalOpen(false)
      closeMentionMenu()
      
      // Expand the message to show the new reply and scroll to it
      const messageWithReplies = await boxApi.getMessage(boxId, messageIdToReply)
      setExpandedMessage(messageWithReplies)
      await loadMessages()
      
      // Auto-scroll to expanded replies after reply is posted
      setTimeout(() => {
        const ref = expandedRepliesRefs.current[messageIdToReply]
        if (ref) {
          ref.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          })
        }
      }, 300)
      
      setReplyingTo(null)
      setReplyToMessage(null)
    } catch (err) {
      setError(`Failed to post reply: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleExpandMessage = async (messageId) => {
    const isExpanding = expandedMessage?.id !== messageId
    if (!isExpanding) {
      setExpandedMessage(null)
      return
    }
    try {
      const messageWithReplies = await boxApi.getMessage(boxId, messageId)
      setExpandedMessage(messageWithReplies)
      
      // Auto-scroll to expanded replies section after a brief delay
      setTimeout(() => {
        const ref = expandedRepliesRefs.current[messageId]
        if (ref) {
          ref.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          })
        }
      }, 150)
    } catch (err) {
      setError(`Failed to load message details: ${err.message}`)
    }
  }

  const handleReplyClick = async (messageId) => {
    try {
      // Load the message details first
      const messageWithReplies = await boxApi.getMessage(boxId, messageId)
      setReplyToMessage(messageWithReplies)
      setReplyingTo(messageId)
      setIsReplyModalOpen(true)
      // Also expand the message to show replies
      setExpandedMessage(messageWithReplies)
      
      // Auto-scroll to expanded replies after modal opens
      setTimeout(() => {
        const ref = expandedRepliesRefs.current[messageId]
        if (ref) {
          ref.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          })
        }
      }, 300)
    } catch (err) {
      setError(`Failed to load message details: ${err.message}`)
    }
  }

  const handleDeleteMessage = (messageId) => {
    setDeleteReason('')
    setDeleteMessageModal({ open: true, messageId })
  }

  const confirmDeleteMessage = async () => {
    const trimmed = (deleteReason || '').trim()
    if (!trimmed) {
      setError('Please provide a reason for deleting this message.')
      return
    }
    try {
      setDeleting(true)
      setError(null)
      await boxApi.deleteMessage(boxId, deleteMessageModal.messageId, { data: { reason: trimmed } })
      await loadMessages()
      if (expandedMessage?.id === deleteMessageModal.messageId) {
        setExpandedMessage(null)
      }
      setDeleteMessageModal({ open: false, messageId: null })
      setDeleteReason('')
    } catch (err) {
      setError(`Failed to delete message: ${err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteReply = (messageId, replyId) => {
    setDeleteReplyModal({ open: true, messageId, replyId })
  }

  const confirmDeleteReply = async () => {
    try {
      setDeleting(true)
      setError(null)
      await boxApi.deleteReply(boxId, deleteReplyModal.messageId, deleteReplyModal.replyId)
      await loadMessages()
      // Refresh expanded message
      const messageWithReplies = await boxApi.getMessage(boxId, deleteReplyModal.messageId)
      setExpandedMessage(messageWithReplies)
      setDeleteReplyModal({ open: false, messageId: null, replyId: null })
    } catch (err) {
      setError(`Failed to delete reply: ${err.message}`)
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading messages...</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Message Wall</h3>
          <p className="text-sm text-gray-500 mt-1">
            Post messages and communicate with box members. Type @ to pick a member (we&apos;ll insert their email),
            or @all anywhere to notify everyone.
          </p>
        </div>
        <button
          onClick={() => setIsNewMessageOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Message
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Messages List */}
      {messages.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <ChatBubbleLeftIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by posting the first message.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <div
              key={message.id}
              className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="p-4">
                {/* Message Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <UserCircleIcon className="h-10 w-10 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900">
                          {message.author_name || message.author_email}
                        </p>
                        {message.is_organizer && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                            <ShieldCheckIcon className="h-3 w-3 mr-1" />
                            Organizer
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {formatDate(message.created_at)}
                        </span>
                        {message.edited && (
                          <span className="text-xs text-gray-400 italic">(edited)</span>
                        )}
                      </div>
                      <h4 className="text-base font-semibold text-gray-900 mt-1">
                        {message.subject}
                      </h4>
                      <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-2">
                    {(message.author_id === currentUser?.uid || canManage) && (
                      <button
                        onClick={() => handleDeleteMessage(message.id)}
                        className="text-gray-400 hover:text-red-600"
                        title="Delete message"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Message Footer */}
                <div className="mt-4 flex items-center space-x-4 text-sm">
                  <button
                    onClick={() => handleExpandMessage(message.id)}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {expandedMessage?.id === message.id
                      ? 'Hide replies'
                      : 'View replies'}
                  </button>
                  <button
                    onClick={() => handleReplyClick(message.id)}
                    className="text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Reply
                  </button>
                </div>
              </div>

              {/* Replies Section */}
              {expandedMessage?.id === message.id && (
                <div 
                  ref={(el) => {
                    if (el) {
                      expandedRepliesRefs.current[message.id] = el
                    } else {
                      delete expandedRepliesRefs.current[message.id]
                    }
                  }}
                  className="border-t border-gray-200 bg-gray-50 p-4 space-y-3"
                >
                  {expandedMessage.replies?.map((reply) => (
                    <div key={reply.id} className="bg-white rounded-md p-3 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-2 flex-1">
                          <UserCircleIcon className="h-8 w-8 text-gray-400" />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900">
                                {reply.author_name || reply.author_email}
                              </p>
                              {reply.is_organizer && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                  <ShieldCheckIcon className="h-3 w-3 mr-0.5" />
                                  Organizer
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                {formatDate(reply.created_at)}
                              </span>
                              {reply.edited && (
                                <span className="text-xs text-gray-400 italic">(edited)</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                              {reply.content}
                            </p>
                          </div>
                        </div>
                        {(reply.author_id === currentUser?.uid || canManage) && (
                          <button
                            onClick={() => handleDeleteReply(message.id, reply.id)}
                            className="text-gray-400 hover:text-red-600 ml-2"
                            title="Delete reply"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New Message Modal */}
      <Dialog
        open={isNewMessageOpen}
        onClose={() => {
          setIsNewMessageOpen(false)
          closeMentionMenu()
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                New Message
              </Dialog.Title>
              <button
                onClick={() => {
                  setIsNewMessageOpen(false)
                  closeMentionMenu()
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitMessage(onSubmitMessage)} className="p-6 space-y-4">
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Subject *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="subject"
                    {...subjectRegistration}
                    ref={(el) => {
                      subjectRef.current = el
                      subjectRegistration.ref(el)
                    }}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white text-gray-900 placeholder-gray-500"
                    placeholder="Enter subject (use @all to notify all members)"
                    onBlur={closeMentionMenu}
                  />
                  {renderMentionMenu('message-subject')}
                </div>
                {messageErrors.subject && (
                  <p className="mt-1 text-sm text-red-600">{messageErrors.subject.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                  Message *
                </label>
                <div className="relative">
                  <textarea
                    id="content"
                    rows={6}
                    {...contentRegistration}
                    ref={(el) => {
                      contentRef.current = el
                      contentRegistration.ref(el)
                    }}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white text-gray-900 placeholder-gray-500"
                    placeholder="Write your message (type @ to mention someone)"
                    onBlur={closeMentionMenu}
                  />
                  {renderMentionMenu('message-content')}
                </div>
                {messageErrors.content && (
                  <p className="mt-1 text-sm text-red-600">{messageErrors.content.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsNewMessageOpen(false)
                    resetMessage()
                    closeMentionMenu()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {submitting ? 'Posting...' : 'Post Message'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Delete Message Modal */}
      <Dialog
        open={deleteMessageModal.open}
        onClose={() => !deleting && setDeleteMessageModal({ open: false, messageId: null })}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="flex items-start space-x-4 rounded-t-2xl px-6 py-5 bg-yellow-50">
              <div className="flex-shrink-0">
                <TrashIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="flex-1">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Delete Message
                </Dialog.Title>
                <Dialog.Description className="mt-2 text-sm text-gray-600">
                  Are you sure you want to delete this message? This action cannot be undone.
                </Dialog.Description>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
              <div>
                <label htmlFor="delete-reason" className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for deletion <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="delete-reason"
                  rows={3}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Explain why you are deleting this message..."
                  disabled={deleting}
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setDeleteMessageModal({ open: false, messageId: null })}
                disabled={deleting}
                className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteMessage}
                disabled={deleting || !deleteReason.trim()}
                className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              >
                {deleting ? 'Deleting...' : 'Delete Message'}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Reply Modal */}
      <Dialog
        open={isReplyModalOpen}
        onClose={() => {
          setIsReplyModalOpen(false)
          setReplyingTo(null)
          setReplyToMessage(null)
          closeMentionMenu()
          resetReply()
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Reply to Message
              </Dialog.Title>
              <button
                onClick={() => {
                  setIsReplyModalOpen(false)
                  setReplyingTo(null)
                  setReplyToMessage(null)
                  closeMentionMenu()
                  resetReply()
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Original message preview */}
            {replyToMessage && (
              <div className="px-6 pt-4 pb-2 border-b border-gray-100 bg-gray-50">
                <div className="text-xs font-medium text-gray-500 mb-2">Replying to:</div>
                <div className="text-sm font-semibold text-gray-900">{replyToMessage.subject}</div>
                <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {replyToMessage.content}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmitReply(onSubmitReply)} className="p-6 space-y-4">
              <div>
                <label htmlFor="reply-content-modal" className="block text-sm font-medium text-gray-700">
                  Your reply *
                </label>
                <div className="relative mt-1">
                  <textarea
                    id="reply-content-modal"
                    rows={6}
                    {...replyRegistration}
                    ref={(el) => {
                      replyRef.current = el
                      replyRegistration.ref(el)
                    }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white text-gray-900 placeholder-gray-500"
                    placeholder="Write your reply (type @ to mention someone)"
                    onBlur={closeMentionMenu}
                  />
                  {renderMentionMenu('reply-content')}
                </div>
                {replyErrors.content && (
                  <p className="mt-1 text-sm text-red-600">{replyErrors.content.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsReplyModalOpen(false)
                    setReplyingTo(null)
                    setReplyToMessage(null)
                    resetReply()
                    closeMentionMenu()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {submitting ? 'Posting...' : 'Post Reply'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Delete Reply Modal */}
      <ConfirmModal
        open={deleteReplyModal.open}
        onClose={() => setDeleteReplyModal({ open: false, messageId: null, replyId: null })}
        onConfirm={confirmDeleteReply}
        title="Delete Reply"
        message="Are you sure you want to delete this reply? This action cannot be undone."
        confirmLabel={deleting ? 'Deleting...' : 'Delete Reply'}
        cancelLabel="Cancel"
        loading={deleting}
        type="warning"
      />
    </div>
  )
}
